import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Briefcase, LayoutDashboard, FileText, Users, Building2, 
  Settings, LogOut, Menu, X, User, CreditCard, ChevronDown, Bell,
  GraduationCap, Shield, Sparkles, MessageSquare, UserPlus, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isRecruiter = ['super_admin', 'client_admin', 'team_leader', 'recruiter'].includes(user?.role);
  const isAdmin = ['super_admin', 'client_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/');
  };

  const navItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { href: '/jobs', label: 'Jobs', icon: Briefcase, show: true },
    { href: '/applications', label: 'Applications', icon: FileText, show: true },
    { href: '/job-alerts', label: 'Alerts', icon: Bell, show: user?.role === 'candidate' },
    { href: '/profile', label: 'Profile', icon: User, show: user?.role === 'candidate' },
    { href: '/resume-builder', label: 'Resume', icon: Sparkles, show: user?.role === 'candidate' },
    { href: '/chat', label: 'Chat', icon: MessageSquare, show: true },
    { href: '/team', label: 'Team', icon: Users, show: isRecruiter },
    { href: '/staff', label: 'Staff', icon: UserPlus, show: isAdmin },
    { href: '/import-export', label: 'Import/Export', icon: ArrowUpDown, show: isAdmin },
    { href: '/organization', label: 'Institution', icon: Building2, show: isAdmin },
    { href: '/admin', label: 'Admin', icon: Shield, show: isSuperAdmin },
  ].filter(item => item.show);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/edassist-logo.png" alt="EdAssist" className="h-8 w-auto object-contain" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link key={item.href} to={item.href}>
                  <Button 
                    variant={isActive(item.href) ? 'secondary' : 'ghost'}
                    className={`gap-2 ${isActive(item.href) ? 'bg-slate-100' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Mobile Menu Toggle */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-600">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">
                      {user?.name}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/pricing')} data-testid="menu-billing">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Billing & Pricing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link 
                  key={item.href} 
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div 
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive(item.href) 
                        ? 'bg-indigo-50 text-indigo-600' 
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </div>
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
